/**
 * Turns an admin's colour-wheel pick into text colours that stay readable.
 *
 * A badge colour is chosen once but rendered on two very different surfaces, so
 * a single hex can only ever be right for one of them. We keep the hue and the
 * saturation the admin chose — that is what they were picking — and move only
 * the lightness until the chip clears 4.5:1 against the surface it sits on.
 *
 * The surfaces below are the *worst case* of each theme: the darkest paper and
 * the lightest ink band in application.css. Anything else is more contrast.
 */

/** --color-paper-550, the page surface under every card. */
export const LIGHT_SURFACE = '#e8ebe7'
/** The raised dark band — lighter than the page, so the harder of the two. */
export const DARK_SURFACE = '#1f2730'

const AA_NORMAL_TEXT = 4.5
/** Grey, for a stored value no wheel could have produced. */
const FALLBACK: Hsl = { h: 0, s: 0, l: 50 }

interface Rgb {
  r: number
  g: number
  b: number
}
interface Hsl {
  h: number
  s: number
  l: number
}

export interface BadgeTextColors {
  light: string
  dark: string
}

/** The same hue at two lightnesses: one for each theme. */
export function badgeTextColors(color: string): BadgeTextColors {
  const hsl = parseHex(color) ?? FALLBACK

  return {
    light: format(adjust(hsl, LIGHT_SURFACE, -1)),
    dark: format(adjust(hsl, DARK_SURFACE, 1)),
  }
}

/** WCAG 2.2 contrast ratio. Takes hex or the `hsl()` strings emitted here. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(toRgb(a)), luminance(toRgb(b))].sort((x, y) => y - x)
  return (light + 0.05) / (dark + 0.05)
}

/* ─── Lightness search ──────────────────────────────────── */

/**
 * Walks lightness one step at a time in `direction` until the colour clears AA
 * against `surface`. Ends at pure black or white, which always clears it.
 */
function adjust(hsl: Hsl, surface: string, direction: 1 | -1): Hsl {
  let candidate = hsl
  for (let i = 0; i <= 100; i += 1) {
    if (contrastRatio(format(candidate), surface) >= AA_NORMAL_TEXT) return candidate
    candidate = { ...candidate, l: clamp(candidate.l + direction) }
  }
  return candidate
}

const clamp = (value: number) => Math.min(100, Math.max(0, value))

/* ─── Conversions ───────────────────────────────────────── */

const format = ({ h, s, l }: Hsl) => `hsl(${round(h)} ${round(s)}% ${round(l)}%)`
const round = (value: number) => Math.round(value * 10) / 10

function toRgb(value: string): Rgb {
  return value.startsWith('hsl(') ? hslToRgb(parseHsl(value)) : (parseHexRgb(value) ?? { r: 0, g: 0, b: 0 })
}

function parseHex(value: string): Hsl | null {
  const rgb = parseHexRgb(value)
  return rgb && rgbToHsl(rgb)
}

function parseHexRgb(value: string): Rgb | null {
  const hex = value.trim().replace(/^#/, '')
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
  if (!/^[0-9a-f]{6}$/i.test(full)) return null

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  }
}

function parseHsl(value: string): Hsl {
  const [h, s, l] = value.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0]
  return { h, s, l }
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const [red, green, blue] = [r / 255, g / 255, b / 255]
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const l = (max + min) / 2
  const delta = max - min
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  return { h: hue(red, green, blue, max, delta), s: s * 100, l: l * 100 }
}

function hue(r: number, g: number, b: number, max: number, delta: number): number {
  if (delta === 0) return 0
  const raw =
    max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4
  return (raw * 60 + 360) % 360
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const [saturation, lightness] = [s / 100, l / 100]
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lightness - c / 2
  const [r, g, b] = rgbTriple(h, c, x)

  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 }
}

function rgbTriple(h: number, c: number, x: number): [number, number, number] {
  const sector = Math.floor(((h % 360) + 360) % 360 / 60)
  const table: [number, number, number][] = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ]
  return table[sector] ?? [0, 0, 0]
}

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
  const [red, green, blue] = [r, g, b].map(channel)
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

function channel(value: number): number {
  const v = value / 255
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}
